"use client";

import { useEffect, useId, useRef } from "react";

import { Tooltip } from "../../../_ui/Tooltip";
import { IconClose } from "./parts";

/*
 * 바닥 시트 — `simple` 의 덮개 원자 (`docs/design/simple-skin/README.md` §5 «시트 — 덮개는 이동이 아니다»).
 *
 * 🔴 **페이지 이동이 아닙니다.** 로그인 유도(02)·약관(10)·취소 확인이 이것을 씁니다. 폼을 채우다
 *    주소를 갈면 입력해 둔 값이 날아가므로, 어느 화면에서 열든 **그 화면 위를 덮고** 닫으면 하던
 *    자리로 돌아옵니다.
 * 🔴 **화면 바닥에 붙습니다**(`fixed`) — 페이지 바닥이 아닙니다. PC(lg)에서는 가운데 창입니다.
 * 🔴 **대화상자 규칙**: `role="dialog"` · `aria-modal` · 열리면 창으로 초점 · Tab 이 밖으로 새지 않음 ·
 *    ESC·바깥 누르기로 닫힘 · 닫히면 연 버튼으로 초점 복귀 · 뒤 화면 스크롤 잠금.
 *    (`_ui/LegalSheet.tsx` 와 같은 규칙 — 그쪽은 `default` 모양이라 simple 은 이 원자로 다시 그립니다.)
 * 🔑 윗모서리는 `--tnf-radius-sheet`(round × 1.1). 손잡이 막대는 모서리가 아니라 알약이라 `round`
 *    를 따라가지 않습니다(README §4).
 *
 * 사용:
 *   <Sheet open={open} onClose={close} title="로그인이 필요합니다" footer={<Action …/>}>본문</Sheet>
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  closeButton = false,
  tall = false,
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  /** 시트 바닥에 고정되는 줄 (약관의 «동의합니다») */
  footer?: React.ReactNode;
  /** 머리 오른쪽 «닫기» 아이콘 버튼 — 긴 본문(약관)에 둡니다 */
  closeButton?: boolean;
  /** 본문이 긴 시트 — 화면 높이의 대부분을 씁니다 */
  tall?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.focus();

    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center lg:items-center">
      <div aria-hidden="true" className="absolute inset-0 bg-[rgb(15_20_28_/_48%)]" onClick={onClose} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative flex w-full flex-col overflow-hidden rounded-t-[var(--tnf-radius-sheet)] bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-12px_40px_-16px_rgb(15_20_28_/_35%)] outline-none lg:mx-6 lg:max-w-[480px] lg:rounded-[var(--tnf-radius-sheet)] lg:pb-0 ${tall ? "h-[min(648px,88dvh)] lg:h-[min(720px,82dvh)]" : "max-h-[88dvh]"}`}
      >
        <div className="flex-none px-5 pt-2.5">
          <span aria-hidden="true" className="mx-auto mb-[15px] block h-1 w-10 rounded-full bg-line lg:hidden" />
          <div className="flex items-start gap-3 lg:pt-3">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-[length:calc(var(--tnf-text-base)*19/14)] font-bold tracking-[-0.03em] text-ink">{title}</h2>
              {subtitle && <div className="mt-[5px] text-caption text-ink-faint">{subtitle}</div>}
            </div>
            {closeButton && (
              <Tooltip text="창을 닫고 하던 화면으로 돌아갑니다." placement="bottom" align="right">
                {(trigger) => (
                  <button
                    type="button"
                    aria-label="닫기"
                    onClick={onClose}
                    className="grid size-9 flex-none place-items-center rounded-[var(--tnf-radius-sm)] bg-raised text-ink-soft hover:text-ink"
                    {...trigger}
                  >
                    <IconClose />
                  </button>
                )}
              </Tooltip>
            )}
          </div>
        </div>
        <div className={`min-h-0 overflow-y-auto px-5 ${tall ? "mt-4 flex-1" : "mt-2.5"} ${footer ? "" : "pb-6"}`}>{children}</div>
        {footer && <div className={`flex-none px-5 pt-3 pb-[22px] ${tall ? "border-t border-line" : ""}`}>{footer}</div>}
      </div>
    </div>
  );
}
